import { QBankStructureStatus, QBankStructureLevelCategory } from '@exam-management/graphql';
import { Flex, type FlexProps, styled } from '@shared/components';

import { QBankStructureLevelItemRowMenu } from '../../components';

import { QBankStructureLevelItemRowWithActionState } from './utils';

interface EventHandlers {
  onDuplicate: () => void;
  onDelete: () => void;
  onSetNonInfo?: () => void;
}

export interface CategoryExpandState {
  category: QBankStructureLevelCategory;
  expanded: boolean;
}

interface RowEndAdornmentProps {
  state: QBankStructureLevelItemRowWithActionState;
  eventHandlers: EventHandlers;
  sx?: FlexProps['sx'];
  categoryExpandState?: CategoryExpandState;
  hasNonInfo?: boolean;
  isNew: boolean;
  qBSStatus: QBankStructureStatus;
  duplicable?: boolean;
  dataTestId?: string;
}

const EndAdornmentWrapper = styled(Flex)({
  minWidth: '48px',
  justifyContent: 'center',
  alignSelf: 'center',
});

export default function RowEndAdornment(props: Readonly<RowEndAdornmentProps>) {
  const {
    state,
    eventHandlers: { onDuplicate, onDelete, onSetNonInfo },
    sx,
    categoryExpandState,
    hasNonInfo = false,
    isNew = false,
    qBSStatus,
    duplicable: isDuplicable = false,
    dataTestId,
  } = props;

  const shouldDisableSetNonInfo =
    (qBSStatus === QBankStructureStatus.Unpublished && !isNew) || hasNonInfo;

  switch (state) {
    case QBankStructureLevelItemRowWithActionState.VIEW:
      return null;
    case QBankStructureLevelItemRowWithActionState.EDIT:
      return (
        <EndAdornmentWrapper
          sx={{
            ...sx,
          }}
        >
          {/* For Category Part, the color of the menu item will be white when expanded */}
          <QBankStructureLevelItemRowMenu
            onDuplicate={isDuplicable ? onDuplicate : undefined}
            data-testid={dataTestId}
            onDelete={onDelete}
            onSetNonInfo={onSetNonInfo}
            disabledSetNonInfo={shouldDisableSetNonInfo}
            sx={(theme) => ({
              color:
                categoryExpandState?.category === QBankStructureLevelCategory.Part &&
                categoryExpandState.expanded
                  ? theme.palette.common.white
                  : theme.palette.grey[500],
              p: theme.spacing(0),
            })}
          />
        </EndAdornmentWrapper>
      );
    case QBankStructureLevelItemRowWithActionState.LOCK:
      return (
        <EndAdornmentWrapper
          sx={{
            ...sx,
          }}
        >
          {/* For Lock State not passing onDelete will disable delete menu item */}
          {/* For Category Part, the color of the menu item will be white when expanded */}
          <QBankStructureLevelItemRowMenu
            onDuplicate={isDuplicable ? onDuplicate : undefined}
            onSetNonInfo={onSetNonInfo}
            disabledSetNonInfo={shouldDisableSetNonInfo}
            data-testid={dataTestId}
            sx={(theme) => ({
              color:
                categoryExpandState?.category === QBankStructureLevelCategory.Part &&
                categoryExpandState.expanded
                  ? theme.palette.common.white
                  : theme.palette.grey[500],
            })}
          />
        </EndAdornmentWrapper>
      );
    default:
      return null;
  }
}
